import * as z from 'zod';
import { localizedTextSchema } from './product';
import { listResponseSchema, optional, timestampSchema } from './common';

// The moderation queue as the MERCHANT sees it — `ModerationService`'s three
// merchant-facing RPCs in `apps/merchant-panel/openapi.json`:
//
//   POST /merchant/entities/{entity_type}/{entity_id}/submit-for-review   hand in
//   POST /merchant/change-requests/{id}/cancel                            withdraw
//   GET  /change-requests/by-merchant                            list own
//
// The admin side of the same service (approve/reject, the global queue) is
// deliberately absent: it is not a merchant-panel surface.
//
// This is the OTHER half of the gating described on `pendingChangeRequestSchema`
// (model/product.ts). That schema models the RECEIPT a gated write hands back
// instead of the entity; these are the requests themselves, addressable and
// listable. The two are intentionally separate shapes — the receipt carries
// three fields, the queue row carries the whole record.

/**
 * Which kind of entity a change request is about (`v1ChangeRequest.entity_type`,
 * and the `{entity_type}` path segment of `submit-for-review`).
 *
 * A bare string on the wire, kept as a closed union here because these are the
 * six the gate covers and the path segment must be one of them — a typo would
 * otherwise 404 at runtime rather than fail to compile. Reads go through
 * {@link changeRequestEntityTypeSchema}, which tolerates anything.
 */
export const CHANGE_REQUEST_ENTITY_TYPES = [
  'restaurant',
  'product',
  'product_category',
  'promotion',
  'restaurant_banner',
  'product_banner',
  'modifier_group',
] as const;
export type ChangeRequestEntityType = (typeof CHANGE_REQUEST_ENTITY_TYPES)[number];

/**
 * The entity type as READ back. Deliberately a plain string, not the union
 * above: a novel entity type added server-side must not fail the whole queue
 * payload, and the UI degrades to showing the raw slug.
 */
export const changeRequestEntityTypeSchema = z.string().default('');

/**
 * Where the request stands. `"pending" | "approved" | "rejected" | "stale" |
 * "withdrawn"` per the spec, but typed as a string for the same
 * degrade-don't-fail reason as the entity type.
 *
 * `stale` is the one that is not a verdict: the underlying entity moved on
 * (another approved request changed it) so this one can no longer be applied.
 * `withdrawn` is what a merchant's own cancel produces.
 */
export const changeRequestStatusSchema = z.string().default('');

/** What the request would do — `"create" | "update" | "delete" | "transition"`. */
export const changeRequestOperationSchema = z.string().default('');

/**
 * One entry in the moderation queue (`v1ChangeRequest`).
 *
 * `payload`/`baseline` are `google.protobuf.Struct` on the wire — deliberately
 * untyped, because the gate is entity-agnostic and a fixed shape per entity
 * would defeat that. They are modelled as unknown records rather than parsed:
 * the merchant screen renders the request's METADATA (what, when, why
 * rejected), never a diff of an arbitrary payload.
 */
export const changeRequestSchema = z.object({
  id: z.string().default(''),
  entity_type: changeRequestEntityTypeSchema,
  /** Absent for a pending CREATE — there is no entity yet to point at. */
  entity_id: optional(z.string()),
  operation: changeRequestOperationSchema,
  merchant_id: optional(z.string()),
  submitted_by: optional(z.string()),
  payload: optional(z.record(z.string(), z.unknown())),
  payload_version: optional(z.number()),
  baseline: optional(z.record(z.string(), z.unknown())),
  baseline_updated_at: optional(timestampSchema),
  status: changeRequestStatusSchema,
  /** Why moderation sent it back. Present only on a `rejected` request. */
  rejection_reason: optional(z.string()),
  reviewed_by: optional(z.string()),
  reviewed_at: optional(timestampSchema),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  /** Set for restaurant-scoped entity types; absent for the rest. */
  restaurant_id: optional(z.string()),
  /**
   * `"manual"` (the merchant panel) or `"sync"` (menu sync). One entity may
   * carry one pending request PER SOURCE, and approving either deletes the
   * other — so this is what tells two competing versions apart.
   */
  source: optional(z.string()),
  /**
   * The entity's own name in every language — what a human reads instead of
   * `entity_id`. Filled on the LIST reads only; absent on a pending create
   * (nothing to name yet) and on the single-request replies of cancel/submit,
   * where the caller already knows what it acted on.
   */
  entity_name: optional(localizedTextSchema),
  original_payload: optional(z.record(z.string(), z.unknown())),
  amended_by: optional(z.string()),
  amended_at: optional(timestampSchema),
});
export type ChangeRequest = z.infer<typeof changeRequestSchema>;

/** `{ list, meta }` envelope of `GET /merchant/change-requests`. */
export const changeRequestListResponseSchema = listResponseSchema(changeRequestSchema);

/**
 * Single-request payload — `data` on submit-for-review and on cancel
 * (`v1SubmitEntityForReviewResponse` / `v1CancelChangeRequestResponse`, the
 * same one-field shape).
 *
 * `change_request` is OPTIONAL: protobuf-JSON omits a message field at its zero
 * value, and neither caller needs the record — the submit's own outcome is
 * "it is queued now" and the cancel's is "it is withdrawn now", both of which
 * the mutation already knows. A required field would fail a request that
 * actually succeeded.
 */
export const changeRequestResponseSchema = z.object({
  change_request: optional(changeRequestSchema),
});
export type ChangeRequestResponse = z.infer<typeof changeRequestResponseSchema>;

/**
 * Request body for `POST /api/v1/merchant/entities/{entity_type}/submit-for-review`.
 *
 * `entity_ids` narrows the call to the listed entities — the panel sends the
 * merchant's current selection. Absent (or empty) means every eligible draft
 * of the type, preserving the original "send all" behavior.
 */
export const bulkSubmitForReviewRequestSchema = z.object({
  created_before: optional(timestampSchema),
  entity_ids: optional(z.array(z.string())),
});
export type BulkSubmitForReviewParams = z.infer<typeof bulkSubmitForReviewRequestSchema>;

/**
 * Result item for a single entity in a bulk submit-for-review response.
 * Either `change_request` on success or `error` on failure.
 */
export const bulkSubmitResultItemSchema = z.object({
  entity_id: z.string().default(''),
  entity_type: changeRequestEntityTypeSchema,
  change_request: optional(changeRequestSchema),
  error: optional(
    z.object({
      code: z.union([z.number(), z.string()]).default(0),
      message: z.string().default(''),
      details: optional(z.unknown()),
    }),
  ),
});
export type BulkSubmitResultItem = z.infer<typeof bulkSubmitResultItemSchema>;

/**
 * Envelope for `POST /api/v1/merchant/entities/{entity_type}/submit-for-review`.
 * Submits all drafts of the specified entity type.
 */
export const bulkSubmitForReviewResponseSchema = z.object({
  results: z.array(bulkSubmitResultItemSchema).default([]),
  submitted_count: z.number().int().default(0),
  failed_count: z.number().int().default(0),
  fiscal_codes_missing: z.array(z.string()).default([]),
  remaining: z.number().int().default(0),
});
export type BulkSubmitForReviewResponse = z.infer<
  typeof bulkSubmitForReviewResponseSchema
>;

/**
 * Entity types supported by merchant bulk-delete —
 * `POST /api/v1/merchant/entities/{entity_type}/bulk-delete`.
 * Any other type will receive 404 from the gateway.
 */
export const BULK_DELETE_ENTITY_TYPES = [
  'restaurant',
  'product',
  'product_category',
  'promotion',
  'modifier_group',
  'modifier_option',
] as const;
export type BulkDeleteEntityType = (typeof BULK_DELETE_ENTITY_TYPES)[number];

/**
 * Entity types supported by admin bulk-delete —
 * `POST /api/v1/admin/entities/{entity_type}/bulk-delete`.
 */
export const ADMIN_BULK_DELETE_ENTITY_TYPES = [
  ...BULK_DELETE_ENTITY_TYPES,
  'restaurant_banner',
  'product_banner',
  'restaurant_category',
  'merchant',
] as const;
export type AdminBulkDeleteEntityType = (typeof ADMIN_BULK_DELETE_ENTITY_TYPES)[number];

/** Error info for a single entity that failed deletion (`v1DeleteEntityError`). */
export const deleteEntityErrorSchema = z.object({
  code: z.union([z.number(), z.string()]).default(0),
  message: z.string().default(''),
  details: optional(z.unknown()),
});
export type DeleteEntityError = z.infer<typeof deleteEntityErrorSchema>;

/**
 * Single entity outcome in bulk deletion (`v1DeleteEntityResult`).
 *
 * Three cases:
 * 1. `change_request` and `error` are empty/null: entity deleted immediately.
 * 2. `change_request` present: deletion is gated and queued for moderation; entity remains until approved.
 * 3. `error` present: this entity failed / was refused; other IDs processed normally.
 */
export const deleteEntityResultSchema = z.object({
  entity_id: z.string().default(''),
  entity_type: z.string().default(''),
  change_request: optional(changeRequestSchema),
  error: optional(deleteEntityErrorSchema),
});
export type DeleteEntityResult = z.infer<typeof deleteEntityResultSchema>;

/** Helper to determine the outcome status for a single entity result. */
export function getDeleteEntityStatus(
  result: DeleteEntityResult,
): 'deleted' | 'pending' | 'failed' {
  if (result.error) return 'failed';
  if (result.change_request) return 'pending';
  return 'deleted';
}

/**
 * Request body for `POST /api/v1/merchant/entities/{entity_type}/bulk-delete`.
 * `ids`: 1 to 100 UUIDs.
 * `restaurant_id`: optional, needed only for modifier_group/modifier_option.
 */
export const bulkDeleteEntitiesRequestSchema = z.object({
  ids: z.array(z.string()).min(1).max(100),
  restaurant_id: optional(z.string()),
});
export type BulkDeleteEntitiesParams = z.infer<typeof bulkDeleteEntitiesRequestSchema>;

/**
 * Response envelope for `POST /api/v1/merchant/entities/{entity_type}/bulk-delete`.
 */
export const bulkDeleteEntitiesResponseSchema = z.object({
  results: z.array(deleteEntityResultSchema).default([]),
  deleted_count: z.number().int().default(0),
  pending_count: z.number().int().default(0),
  failed_count: z.number().int().default(0),
  remaining_ids: z.array(z.string()).default([]),
});
export type BulkDeleteEntitiesResponse = z.infer<typeof bulkDeleteEntitiesResponseSchema>;
